import React, { useEffect, useMemo, useState } from "react";
import { Box, MultiSelect, Stack, Text, TextInput, Textarea, Title } from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import { EditorDrawer } from "@features/Doctype/FlowConfig/EditorDrawer";
import type { AccessScopeKind, Role, RoleModule } from "@models/access";
import { ApiError } from "@models/error";
import { accessApi } from "@services/accessApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import { roleKeys, useEscalationGuard } from "@hooks/access";
import PermissionPicker from "./PermissionPicker";

const ACCESS_SCOPES: AccessScopeKind[] = ["GLOBAL", "CUSTOMER_PORTFOLIO", "ORGANISATION_HIERARCHY"];

interface RoleEditorDrawerProps {
  opened: boolean;
  /** The role to edit; `null` creates one. */
  role: Role | null;
  /** Every permission of the application, by module (from any role or the catalogue). */
  modules: RoleModule[];
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface Draft {
  name: string;
  description: string;
  accessScopes: AccessScopeKind[];
  keys: string[];
}

const draftOf = (role: Role | null): Draft => ({
  name: role?.name ?? "",
  description: role?.description ?? "",
  accessScopes: role?.accessScopes ?? ["ORGANISATION_HIERARCHY"],
  keys: role ? roleKeys(role).sort() : [],
});

/**
 * Create or edit a role: name, description, access scopes and its permissions.
 * BeeHive replaces a role's whole permission list on update, so the save sends
 * the role's `updatedAt` and a concurrent change comes back as 409 ROLE_CHANGED.
 */
const RoleEditorDrawer: React.FC<RoleEditorDrawerProps> = ({ opened, role, modules, canManage, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { held, canEditRole } = useEscalationGuard();
  const [draft, setDraft] = useState<Draft>(() => draftOf(role));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setDraft(draftOf(role));
    setError(null);
  }, [opened, role]);

  const editable = canManage && (role === null || canEditRole(role));
  const readOnlyReason = !canManage ? null
    : role?.heldByActor ? t("admin.access.roles.editor.heldByYou")
    : role && !canEditRole(role) ? t("admin.access.roles.editor.tooStrong")
    : null;

  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(role));
  const selected = useMemo(() => new Set(draft.keys), [draft.keys]);

  const save = async () => {
    setSaving(true);
    setError(null);
    const body = {
      name: draft.name.trim(), description: draft.description.trim(),
      permissionKeys: draft.keys, accessScopes: draft.accessScopes,
    };
    try {
      if (role) await accessApi().updateRole(role.id, { ...body, expectedUpdatedAt: role.updatedAt });
      else await accessApi().createRole(body);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError && err.errorCode === "ROLE_CHANGED"
        ? t("admin.access.roles.editor.changed")
        : errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const impact = role
    ? t("admin.access.roles.editor.impact", { count: role.assignedUsers, inScope: role.assignedUsersInScope })
    : null;

  return (
    <EditorDrawer
      opened={opened}
      onClose={onClose}
      title={
        <Box>
          <Title order={4}>{role ? role.name : t("admin.access.roles.editor.createTitle")}</Title>
          {impact && <Text size="xs" c="orange.8">{impact}</Text>}
        </Box>
      }
      dirty={dirty}
      saving={saving}
      error={error}
      onDismissError={() => setError(null)}
      saveLabel={role ? t("admin.access.roles.editor.save") : t("admin.access.roles.editor.create")}
      blockedReason={dirty && !draft.name.trim() ? t("admin.access.roles.editor.nameRequired") : null}
      onSave={save}
      canEdit={editable}
    >
      <Stack gap="md">
        {readOnlyReason && <Alert color="gray">{readOnlyReason}</Alert>}
        <TextInput
          label={t("admin.access.roles.editor.name")}
          value={draft.name}
          disabled={!editable}
          onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
          required
        />
        <Textarea
          label={t("admin.access.roles.editor.description")}
          value={draft.description}
          disabled={!editable}
          autosize
          minRows={2}
          onChange={(e) => setDraft({ ...draft, description: e.currentTarget.value })}
        />
        <MultiSelect
          label={t("admin.access.roles.editor.accessScopes")}
          data={ACCESS_SCOPES.map((s) => ({ value: s, label: t(`admin.access.roles.scopes.${s}`) }))}
          value={draft.accessScopes}
          disabled={!editable}
          onChange={(v) => setDraft({ ...draft, accessScopes: v as AccessScopeKind[] })}
          comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
        />
        <Text fw={600} size="sm">{t("admin.access.roles.editor.permissions", { count: draft.keys.length })}</Text>
        <PermissionPicker
          modules={modules}
          selected={selected}
          onChange={(next) => setDraft({ ...draft, keys: [...next].sort() })}
          locked={(key) => !held.has(key)}
          readOnly={!editable}
        />
      </Stack>
    </EditorDrawer>
  );
};

export default RoleEditorDrawer;
